const FALLBACK_CURRENCY = "GHS";

const numberOr = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") return fallback;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
};

const identityVolume = (line) =>
  line?.variableVolume && line.volume !== null && line.volume !== undefined
    ? Number(line.volume)
    : null;

export const lineKey = (line) =>
  [
    line?.productId ?? "",
    line?.containerMaterialId ?? "",
    identityVolume(line) ?? "",
  ].join("|");

export const sameLine = (left, right) => lineKey(left) === lineKey(right);

export const LINE_KIND = {
  NEW: "new",
  SWAP: "swap",
  CUSTOM: "custom",
  ACCESSORY: "accessory",
};

const KIND_BY_CODE = {
  NEW_FILLED: LINE_KIND.NEW,
  SWAP_REFILL: LINE_KIND.SWAP,
  CUSTOM_FILL: LINE_KIND.CUSTOM,
  ACCESSORY: LINE_KIND.ACCESSORY,
};

const resolveKind = ({ code, accessory, variableVolume, volume, productVolume }) => {
  if (code && KIND_BY_CODE[code]) return KIND_BY_CODE[code];

  if (accessory) return LINE_KIND.ACCESSORY;

  if (variableVolume && productVolume !== null && volume !== null && volume < productVolume) {
    return LINE_KIND.CUSTOM;
  }

  return LINE_KIND.NEW;
};

const materialAmount = (product, containerMaterialId) => {
  if (containerMaterialId === null || containerMaterialId === undefined) return null;

  const material = (product?.containerMaterials || []).find(
    (row) => String(row?.id) === String(containerMaterialId)
  );

  return numberOr(material?.amount?.value);
};

export const mapCartLine = (row) => {
  const product = row?.product || {};
  const containerOption = product.containerOption || {};
  const variableVolume = containerOption.variableVolume === true;
  const accessory = containerOption.accessory === true;
  const volume = row?.volume ?? null;
  const productVolume = Number.isFinite(Number(product?.volume))
    ? Number(product.volume)
    : null;
  const inStock = row?.inStock !== false && product?.inStock !== false;
  const containerMaterialId = row?.containerMaterial?.id ?? null;
  const quantity = Number(row?.quantity) || 0;

  const currency =
    row?.amount?.currency || product?.amount?.currency || FALLBACK_CURRENCY;
  const lineTotal = numberOr(row?.amount?.value);
  const unitFromLine = lineTotal !== null && quantity > 0 ? lineTotal / quantity : null;
  const unitFromMaterial = materialAmount(product, containerMaterialId);
  const unitFromProduct = numberOr(product?.amount?.value);
  const unitValue = unitFromLine ?? unitFromMaterial ?? unitFromProduct;

  const unitAmount = unitValue !== null ? { value: unitValue, currency } : null;
  const lineAmount =
    lineTotal !== null
      ? { value: lineTotal, currency }
      : unitAmount
      ? { value: unitAmount.value * quantity, currency }
      : null;

  return {
    productId: product.id ?? null,
    containerMaterialId,
    volume,
    quantity,
    supplierId: product.supplier?.id ?? null,
    variableVolume,
    inStock,
    kind: resolveKind({
      code: containerOption.code,
      accessory,
      variableVolume,
      volume,
      productVolume,
    }),
    name: product.name || "",
    imageUrl: (product.imageUrls || [])[0] || null,
    unitAmount,
    lineAmount,
    containerOptionTitle: containerOption.defaultTitle || null,
    containerTypeTitle: product.containerType?.defaultTitle || null,
    containerMaterialTitle: row?.containerMaterial?.defaultTitle || null,
    fuelTypeTitle: product.fuelType?.defaultTitle || null,
  };
};

export const bucketLines = (lines) => {
  const buckets = {
    cylinderLines: [],
    accessoryLines: [],
    customLines: [],
    outOfStockLines: [],
  };

  (lines || []).forEach((line) => {
    if (!line.inStock) {
      buckets.outOfStockLines.push(line);
      return;
    }

    if (line.kind === LINE_KIND.ACCESSORY) buckets.accessoryLines.push(line);
    else if (line.kind === LINE_KIND.CUSTOM) buckets.customLines.push(line);
    else buckets.cylinderLines.push(line);
  });

  return buckets;
};

const isSupplierOnline = (status) =>
  String(status ?? "online").toLowerCase() !== "offline";

export const mapCart = (cart) => {
  if (!cart) return null;

  const rows = cart.cartProducts || cart.products || [];
  const lines = rows.map(mapCartLine);
  const buckets = bucketLines(lines);
  const itemCount = lines.reduce((sum, line) => sum + (line.quantity || 0), 0);

  const supplier = cart.supplier || {};
  const currency =
    lines.find((line) => line.lineAmount)?.lineAmount?.currency || FALLBACK_CURRENCY;
  const productsAmount = numberOr(cart.productsAmount?.value ?? cart.productsAmount);
  const total = numberOr(
    cart.totalCartAmount?.value ?? cart.totalCartAmount ?? productsAmount
  );

  return {
    cartId: cart.cartId ?? cart.id ?? null,
    supplierId: supplier.id ?? lines.find((line) => line.supplierId !== null)?.supplierId ?? null,
    supplierName: supplier.name || null,
    supplierLogoUrl: supplier.referenceUrlLogo || supplier.logoUrl || null,
    supplierOnline: isSupplierOnline(supplier.status),
    lines,
    itemCount,
    productsVolume: numberOr(cart.productsVolume),
    productsAmount,
    productsAmountVAT: numberOr(cart.productsAmountVAT?.value ?? cart.productsAmountVAT),
    couponDiscount: numberOr(cart.couponDiscount?.value ?? cart.couponDiscount),
    total: total !== null ? Math.max(0, total) : null,
    currency,
    couponCode: cart.coupon?.code || null,
    couponTitle: cart.coupon?.defaultTitle || null,
    couponApplicableProductIds: cart.couponApplicableProductIds || [],
    hasOutOfStock: buckets.outOfStockLines.length > 0,
    hasCylinders: buckets.cylinderLines.length > 0,
    hasAccessories: buckets.accessoryLines.length > 0,
    ...buckets,
  };
};

export const mapCartSummary = (carts) => {
  const mapped = (carts || [])
    .map(mapCart)
    .filter((cart) => cart && cart.lines.length > 0);

  const itemCount = mapped.reduce((sum, cart) => sum + cart.itemCount, 0);
  const total = mapped.reduce((sum, cart) => sum + (cart.total || 0), 0);
  const currency = mapped[0]?.currency || FALLBACK_CURRENCY;
  const hasOutOfStock = mapped.some((cart) => cart.hasOutOfStock);

  return {
    carts: mapped,
    itemCount,
    total,
    currency,
    isEmpty: mapped.length === 0,
    hasOutOfStock,
  };
};

export const toPayload = (lines) =>
  (lines || [])
    .filter((line) => line.productId !== null && line.quantity > 0)
    .map((line) => ({
      productId: line.productId,
      quantity: line.quantity,
      ...(line.containerMaterialId !== null && line.containerMaterialId !== undefined
        ? { containerMaterialId: line.containerMaterialId }
        : {}),
      ...(line.variableVolume && line.volume !== null && line.volume !== undefined
        ? { volume: line.volume }
        : {}),
    }));

export const upsertQuantity = (basket, item, mode = "increment") => {
  const rows = [...(basket || [])];
  const index = rows.findIndex((row) => sameLine(row, item));

  if (index === -1) {
    return [...rows, { ...item, quantity: Math.max(1, item.quantity || 1) }];
  }

  const current = rows[index];
  const added = Math.max(1, item.quantity || 1);

  rows[index] = {
    ...current,
    ...item,
    quantity: mode === "increment" ? current.quantity + added : added,
  };

  return rows;
};

export const hasLine = (basket, item) =>
  (basket || []).some((row) => sameLine(row, item) && row.quantity > 0);

export const findLine = (basket, item) =>
  (basket || []).find((row) => sameLine(row, item)) ?? null;

export const removeLine = (basket, item) =>
  (basket || []).filter((row) => !sameLine(row, item));

export const decrementQuantity = (basket, item, by = 1) => {
  const rows = [...(basket || [])];
  const index = rows.findIndex((row) => sameLine(row, item));

  if (index === -1) return rows;

  const next = rows[index].quantity - Math.max(1, by || 1);

  if (next <= 0) {
    rows.splice(index, 1);

    return rows;
  }

  rows[index] = { ...rows[index], quantity: next };

  return rows;
};

export const basketSupplierId = (basket) =>
  (basket || []).find((line) => line.supplierId !== null && line.supplierId !== undefined)
    ?.supplierId ?? null;
